import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { appConfirmMessage } from '../../../utils/appConfirm';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import CreateService from './CreateService';
import ServicesHome from './ServicesHome';
import ServiceDetail from './ServiceDetail';

import residential from '../../../assets/ServiceDashboard/Residential.png';
import OfficeCleaning from '../../../assets/ServiceDashboard/OfficeCleaning.png';
import Store from '../../../assets/ServiceDashboard/Store.png';
import Medical from '../../../assets/ServiceDashboard/Medical.png';
import EndLease from '../../../assets/ServiceDashboard/EndLease.png';
import StrataCleaning from '../../../assets/ServiceDashboard/StrataCleaning.png';
import KitchenCleaning from '../../../assets/ServiceDashboard/KitchenCleaning.png';
import KitchenExhaust from '../../../assets/ServiceDashboard/KitchenExhaust.png';
import DeepClean from '../../../assets/ServiceDashboard/deep Cleaning.png';

import '../styles/ServiceDashboard.css';
import '../styles/ServicesHome.css';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { badgeOf } from './serviceCatalog';
import { useAdminListOverlay } from '../../../utils/useAdminPageSearch';

const iconMap = {
  'Residential Cleaning': residential,
  'Office Cleaning': OfficeCleaning,
  'Store Cleaning': Store,
  'Medical Cleaning': Medical,
  'End of Lease Cleaning': EndLease,
  'Strata Cleaning': StrataCleaning,
  'Kitchen Cleaning': KitchenCleaning,
  'Kitchen Exhaust Cleaning': KitchenExhaust,
  'Deep Cleaning Service': DeepClean,
};

function formatServiceRow(s, index, inactive = false) {
  const avg = Number(s.averageRating || 0);
  const upload = String(s.uploads || '');
  const signed = typeof s.signUrls === 'string' ? s.signUrls.trim() : '';
  const hasImage = Boolean(
    signed || upload.startsWith('data:') || upload.startsWith('http'),
  );
  const icon =
    signed ||
    (upload.startsWith('data:') || upload.startsWith('http') ? upload : null) ||
    iconMap[s.service] ||
    residential;
  return {
    id: s.id,
    name: s.service,
    description: s.description,
    code: s.serviceId || `SRV-${index + 1}`,
    totalProjects: Number(s.serviceCount) || 0,
    rating: avg > 0 ? avg.toFixed(1) : 'N/A',
    reviewCount: s.reviewCount || 0,
    status: inactive || String(s.status || '').toLowerCase() === 'inactive' ? 'inactive' : 'active',
    category: s.category || '',
    badge: badgeOf({ category: s.category, name: s.service }),
    createdAt: s.createdAt || null,
    updatedAt: s.updatedAt || null,
    catalog: s.catalog && typeof s.catalog === 'object' ? s.catalog : { tiers: [] },
    hasImage,
    icon,
  };
}

const ServiceDashboard = () => {
  const location = useLocation();
  const [services, setServices] = useState([]);
  const [inactiveServices, setInactiveServices] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const catalog = useMemo(
    () => [...services, ...inactiveServices],
    [services, inactiveServices],
  );
  const overlay = useAdminListOverlay(catalog);

  // ✅ Fetch services from backend
  // ✅ Fetch services from backend including totalProjects
  const fetchServices = useCallback(async (silent = false) => {
    try {
      if (silent) {
        setRefreshing(true);
      }

      const serviceQuery = `
        query GetServiceDashboard($includeSignUrls: Boolean) {
          getServiceDashboard(includeSignUrls: $includeSignUrls) {
            active {
              id
              service
              description
              serviceId
              uploads
              signUrls
              serviceCount
              averageRating
              reviewCount
              status
              category
              catalog
              createdAt
              updatedAt
            }
            inactive {
              id
              service
              description
              serviceId
              uploads
              signUrls
              serviceCount
              averageRating
              reviewCount
              status
              category
              catalog
              createdAt
              updatedAt
            }
          }
        }
      `;

      const data = await graphqlRequest(serviceQuery, {
        includeSignUrls: true,
      });
      const dashboard = data?.getServiceDashboard;
      const fetchedServices = dashboard?.active || [];
      const fetchedInactive = dashboard?.inactive || [];

      setServices(fetchedServices.map((row, index) => formatServiceRow(row, index, false)));
      setInactiveServices(fetchedInactive.map((row, index) => formatServiceRow(row, index, true)));
    } catch (err) {
      console.error('Error fetching services:', err);
      if (!silent) toast.error('Failed to load services');
    } finally {
      setInitialLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const showModal = overlay.showAdd;
  const selectedService = overlay.viewing;

  // ✅ handle add service and refresh list
  const handleAddService = async (newService) => {
    if (newService?.id) {
      const row = formatServiceRow(
        {
          id: newService.id,
          service: newService.name,
          description: newService.description,
          serviceId: newService.code,
          serviceCount: 0,
          averageRating: 0,
          reviewCount: 0,
          status: newService.active === false ? 'INACTIVE' : 'ACTIVE',
          category: newService.category || '',
          uploads: newService.icon || null,
        },
        services.length,
        newService.active === false,
      );
      if (newService.active === false) setInactiveServices((prev) => [row, ...prev]);
      else setServices((prev) => [row, ...prev]);
    }
    overlay.closeOverlay();
    fetchServices(true);
  };

  const toggleSelectId = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const inactiveIdSet = new Set(inactiveServices.map((s) => s.id));

  const handleMakeInactiveSelected = async () => {
    const activeIds = selectedIds.filter((id) => !inactiveIdSet.has(id));
    if (activeIds.length === 0) return;
    if (
      !await appConfirmMessage(
        `Make ${activeIds.length} service(s) inactive? They will move to the inactive section below.`,
      )
    ) {
      return;
    }

    try {
      setRefreshing(true);
      await Promise.all(
        activeIds.map((serviceId) =>
          graphqlRequest(
            `mutation UpdateServiceStatus($updateService: UpdateService!) {
              updateService(updateService: $updateService) { id status }
            }`,
            { updateService: { id: serviceId, status: 'INACTIVE' } },
          ),
        ),
      );
      toast.success(
        activeIds.length === 1
          ? 'Service marked inactive.'
          : `${activeIds.length} services marked inactive.`,
      );
      setSelectedIds([]);
      setSelectMode(false);
      await fetchServices(true);
    } catch (err) {
      console.error('Error deactivating services:', err);
      toast.error('Could not mark inactive: ' + (err.message || 'Unknown error'));
    } finally {
      setRefreshing(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return;
    if (
      !await appConfirmMessage(
        `Permanently delete ${selectedIds.length} service(s)? This cannot be undone.`,
      )
    ) {
      return;
    }

    try {
      setRefreshing(true);
      await Promise.all(
        selectedIds.map((serviceId) =>
          graphqlRequest(`mutation { removeService(id: "${serviceId}") }`),
        ),
      );
      const count = selectedIds.length;
      setSelectedIds([]);
      setSelectMode(false);
      await fetchServices(true);
      toast.success(count === 1 ? 'Service deleted.' : `${count} services deleted.`);
    } catch (err) {
      console.error('Error deleting services:', err);
      toast.error('Delete failed: ' + err.message);
    } finally {
      setRefreshing(false);
    }
  };

  const handleReactivateSelected = async () => {
    const inactiveIds = selectedIds.filter((id) =>
      inactiveServices.some((s) => s.id === id),
    );
    if (inactiveIds.length === 0) return;
    if (
      !await appConfirmMessage(
        `Reactivate ${inactiveIds.length} service(s)? They will appear in the active list again.`,
      )
    ) {
      return;
    }

    try {
      setRefreshing(true);
      await Promise.all(
        inactiveIds.map((serviceId) =>
          graphqlRequest(
            `mutation UpdateServiceStatus($updateService: UpdateService!) {
              updateService(updateService: $updateService) { id status }
            }`,
            { updateService: { id: serviceId, status: 'ACTIVE' } },
          ),
        ),
      );
      toast.success(
        inactiveIds.length === 1
          ? 'Service reactivated.'
          : `${inactiveIds.length} services reactivated.`,
      );
      setSelectedIds([]);
      setSelectMode(false);
      await fetchServices(true);
    } catch (err) {
      console.error('Error reactivating services:', err);
      toast.error('Could not reactivate: ' + (err.message || 'Unknown error'));
    } finally {
      setRefreshing(false);
    }
  };

  const handleCancelSelect = () => {
    setSelectMode(false);
    setSelectedIds([]);
  };

  const selectedInactiveCount = selectedIds.filter((id) => inactiveIdSet.has(id)).length;
  const selectedActiveCount = selectedIds.length - selectedInactiveCount;

  const urlSearch = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    return (params.get('search') || '').trim().toLowerCase();
  }, [location.search]);

  const matchesFilters = (service) => {
    if (!urlSearch) return true;
    return [service.name, service.description, service.serviceId, service.code]
      .some((field) => String(field || '').toLowerCase().includes(urlSearch));
  };

  const visibleActive = services.filter(matchesFilters);
  const visibleInactive = inactiveServices.filter(matchesFilters);

  const allServices = [...visibleActive, ...visibleInactive];

  const deleteService = async (service = selectedService) => {
    if (!service) return;
    if (!await appConfirmMessage(`Delete ${service.name}? This cannot be undone.`)) return;
    try {
      setRefreshing(true);
      await graphqlRequest(`mutation { removeService(id: "${service.id}") }`);
      toast.success('Service deleted');
      if (selectedService?.id === service.id) overlay.closeOverlay();
      await fetchServices(true);
    } catch (err) {
      toast.error(err?.message || 'Could not delete the service');
    } finally {
      setRefreshing(false);
    }
  };

  const archiveOne = async (service) => {
    if (!service) return;
    const next = service.status === 'inactive' ? 'ACTIVE' : 'INACTIVE';
    try {
      await graphqlRequest(
        `mutation ArchiveService($updateService: UpdateService!) {
          updateService(updateService: $updateService) { id status }
        }`,
        {
          updateService: {
            id: service.id,
            service: service.name,
            description: service.description || ' ',
            serviceId: service.code,
            status: next,
          },
        },
      );
      toast.success(next === 'INACTIVE' ? 'Service archived' : 'Service restored');
      await fetchServices(true);
    } catch (err) {
      toast.error(err?.message || 'Could not update the service');
    }
  };

  return (
    <div
      className={`dashboard-container-service${
        selectedService ? ' dashboard-container-service--profile' : ''
      }`}
    >
      {initialLoading ? (
        <p className="services-loading">Loading services…</p>
      ) : !selectedService ? (
        <>
          {refreshing && <p className="services-refresh-hint">Updating…</p>}
          <ServicesHome
            services={allServices}
            urlSearch={urlSearch}
            onOpen={(service) => overlay.openView(service)}
            onAdd={() => overlay.openAdd()}
            selectMode={selectMode}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelectId}
            onStartSelect={() => setSelectMode(true)}
            onCancelSelect={handleCancelSelect}
            onInactive={handleMakeInactiveSelected}
            onReactivate={handleReactivateSelected}
            onDelete={handleDeleteSelected}
            onToggleActive={(service) => archiveOne(service)}
            onDeleteOne={(service) => deleteService(service)}
            selectedActiveCount={selectedActiveCount}
            selectedInactiveCount={selectedInactiveCount}
          />
          <CreateService
            isOpen={showModal}
            onClose={overlay.closeOverlay}
            onSave={handleAddService}
          />
        </>
      ) : (
        <ServiceDetail
          service={selectedService}
          onBack={overlay.closeOverlay}
          onSave={(updates) => {
            const apply = (list) =>
              list.map((row) =>
                row.id === selectedService?.id ? { ...row, ...updates } : row,
              );
            setServices(apply);
            setInactiveServices(apply);
            fetchServices(true);
          }}
          onDelete={() => deleteService(selectedService)}
        />
      )}
    </div>
  );
};

export default ServiceDashboard;
